/**
 * Marker + copy helpers for `deck.details.archetypes.profile`. Mirrors
 * `deckSynergyProfileMarkers.js` and `deckComboProfileMarkers.js`. The
 * backend ships opaque ids; this module owns the user-facing copy and
 * never invents positioning vocabulary ("cedh" / "casual" / etc.) — see
 * the contract in api-changes-archetypes.md.
 *
 * Unknown ids on any section MUST no-op (the API doc guarantees the id
 * catalog is append-only). All `derive*` helpers below silently drop
 * entries with no registered label.
 */

import { getPrettyLabel } from './CategoryPrettyDisplayUtils';

// ---- Driver registry --------------------------------------------------
// Drivers explain *why* the dominant identity won. Tone leans on whether
// the driver is a structural pattern (alert), a positive build signal
// (ok), a hybrid/spread observation (cool), or a load notice (warn).

const DRIVER_META = {
    primaryLead: {
        tone: 'alert',
        label: ({ data }) => {
            const major = getPrettyLabel(data?.major) || 'Primary archetype';
            const pct = data?.share != null ? Math.round(data.share * 100) : null;
            return pct != null ? `${major} carries ${pct}%` : `${major} leads`;
        },
    },
    primaryRunaway: {
        tone: 'alert',
        label: ({ data }) => {
            const pct = data?.share != null ? Math.round(data.share * 100) : null;
            return pct != null ? `Runaway primary (${pct}%)` : 'Runaway primary';
        },
    },
    secondarySupport: { tone: 'cool', label: () => 'Secondary major supports' },
    subLead: {
        tone: 'alert',
        label: ({ data }) => {
            const sub = getPrettyLabel(data?.sub) || data?.sub;
            return sub ? `${sub} drives the lead` : 'Sub-archetype drives the lead';
        },
    },
    commanderInPrimary: { tone: 'ok', label: () => 'Commander sits in the primary sub' },
    commanderInCombo: { tone: 'ok', label: () => 'Commander is a combo piece' },
    comboWinconLoad: {
        tone: 'alert',
        label: () => 'Multiple wincon combos',
    },
    enablerComboLoad: { tone: 'warn', label: () => 'Enabler-only combo load' },
    kindredCriticalMass: {
        tone: 'ok',
        label: () => 'Typal critical mass',
    },
    kindredCommanderSynergy: { tone: 'ok', label: () => 'Commander cares about the type' },
    staxCriticalMass: { tone: 'alert', label: () => 'Stax critical mass' },
    theftPresence: { tone: 'cool', label: () => 'Theft package present' },
    landsmatterPresence: { tone: 'cool', label: () => 'Lands-matter package present' },
    enchantressPresence: { tone: 'cool', label: () => 'Enchantress package present' },
    superfriendsPresence: { tone: 'cool', label: () => 'Superfriends package present' },
    extraTurnLoop: { tone: 'alert', label: () => 'Extra-turn loop' },
    extraCombatLoop: { tone: 'alert', label: () => 'Extra-combat loop' },
    voltronScaling: { tone: 'cool', label: () => 'Voltron scaling support' },
    battlecruiserCurve: { tone: 'cool', label: () => 'Battlecruiser curve' },
    stormShell: { tone: 'alert', label: () => 'Storm shell' },
    hybridBlend: { tone: 'cool', label: () => 'Top two majors are tied' },
    diffuseTree: { tone: 'warn', label: () => 'Identity is spread thin' },
};

// ---- Hybrid signal registry -------------------------------------------
// Cross-marker overlap notes — readable as "deck blends X and Y."

const HYBRID_META = {
    combatPlusCombo: { tone: 'cool', label: 'Combat × combo' },
    staxPlusCombo: { tone: 'alert', label: 'Prison combo (stax + combo)' },
    landsPlusCombo: { tone: 'cool', label: 'Lands × combo' },
    tokensPlusAnthem: { tone: 'cool', label: 'Tokens × anthem' },
    tokensPlusAristocrats: { tone: 'cool', label: 'Tokens × aristocrats' },
    reanimatorPlusCombo: { tone: 'alert', label: 'Reanimator × combo' },
    blinkPlusCombo: { tone: 'alert', label: 'Blink × combo' },
    blinkPlusTokens: { tone: 'cool', label: 'Blink × tokens' },
    kindredPlusCombat: { tone: 'cool', label: 'Typal × combat' },
    kindredPlusCombo: { tone: 'alert', label: 'Typal × combo' },
    controlPlusCombo: { tone: 'alert', label: 'Control × combo' },
    controlPlusKindred: { tone: 'cool', label: 'Control × typal' },
    theftPlusCombo: { tone: 'alert', label: 'Theft × combo' },
    groupSlugPlusPillowfort: { tone: 'cool', label: 'Punisher (group slug + pillowfort)' },
    superfriendsPlusPillowfort: { tone: 'cool', label: 'Superfriends × pillowfort' },
    extraTurnsPlusCombat: { tone: 'alert', label: 'Extra turns × combat' },
    extraTurnsPlusCombo: { tone: 'alert', label: 'Extra turns × combo' },
    voltronPlusPillowfort: { tone: 'cool', label: 'Voltron × pillowfort' },
};

// ---- Pre-game disclosure registry -------------------------------------

const DISCLOSURE_META = {
    comboIdentity: {
        tone: 'alert',
        label: ({ data }) => {
            const count = data?.comboCount;
            return count ? `Runs ${count} combo line${count === 1 ? '' : 's'}` : 'Has a combo identity';
        },
    },
    comboWinconIdentity: {
        tone: 'alert',
        label: ({ data }) => {
            const wincons = data?.winconCount;
            return wincons ? `${wincons} wincon line${wincons === 1 ? '' : 's'}` : 'Wincon combo identity';
        },
    },
    commanderComboIdentity: { tone: 'alert', label: () => 'Commander is in a combo line' },
    extraTurnsIdentity: { tone: 'alert', label: () => 'Plays extra turns' },
    extraCombatsIdentity: { tone: 'alert', label: () => 'Plays extra combats' },
    massLandDenialIdentity: { tone: 'alert', label: () => 'Runs mass land denial' },
    staxIdentity: { tone: 'alert', label: () => 'Runs stax' },
    pillowfortIdentity: { tone: 'warn', label: () => 'Runs pillowfort' },
    theftIdentity: { tone: 'warn', label: () => 'Runs theft effects' },
    groupSlugIdentity: { tone: 'warn', label: () => 'Runs group slug' },
    groupHugIdentity: { tone: 'cool', label: () => 'Runs group hug' },
    poisonIdentity: { tone: 'alert', label: () => 'Wins via poison' },
    goadIdentity: { tone: 'cool', label: () => 'Goads opponents' },
    millIdentity: { tone: 'warn', label: () => 'Mills opponents' },
    stormIdentity: { tone: 'alert', label: () => 'Storm wincon' },
    reanimatorIdentity: { tone: 'warn', label: () => 'Reanimator strategy' },
    kindredIdentity: {
        tone: 'cool',
        label: ({ data }) => {
            const type = data?.type;
            return type ? `${getPrettyLabel(type) || type} typal` : 'Typal deck';
        },
    },
};

// ---- Tuning suggestion registry ---------------------------------------

const TUNING_META = {
    leanIntoPrimary: {
        tone: 'cool',
        label: ({ data }) => {
            const major = getPrettyLabel(data?.major);
            return major ? `Lean further into ${major}` : 'Lean into the primary archetype';
        },
    },
    decideBetweenTopTwo: { tone: 'warn', label: () => 'Pick a lane between the top two majors' },
    thinDiffuseTree: { tone: 'warn', label: () => 'Thin out the diffuse archetype tree' },
    addKindredCriticalMass: { tone: 'cool', label: () => 'Add typal critical mass' },
    addComboRedundancy: { tone: 'cool', label: () => 'Add combo redundancy' },
    addWinconForEnablers: { tone: 'warn', label: () => 'Add a wincon for the enablers' },
    addVoltronSupport: { tone: 'cool', label: () => 'Add voltron support' },
    addStaxRedundancy: { tone: 'cool', label: () => 'Add stax redundancy' },
    addLandsPayoffs: { tone: 'cool', label: () => 'Add lands-matter payoffs' },
    addEnchantressEnablers: { tone: 'cool', label: () => 'Add enchantress enablers' },
    thinOverlappingSub: {
        tone: 'warn',
        label: ({ data }) => {
            const subs = Array.isArray(data?.subs) ? data.subs : [];
            const named = subs.map((s) => getPrettyLabel(s) || s).filter(Boolean);
            if (named.length === 2) {
                return `Pick between ${named[0]} and ${named[1]}`;
            }
            return 'Trim overlapping sub-archetypes';
        },
    },
};

// ---- Mappers ---------------------------------------------------------

function mapEntries(items, registry) {
    if (!Array.isArray(items)) return [];
    return items
        .map((entry) => {
            const meta = registry[entry?.id];
            if (!meta) return null;
            const label = typeof meta.label === 'function' ? meta.label(entry || {}) : meta.label;
            if (!label) return null;
            return { key: entry.id, label, tone: meta.tone };
        })
        .filter(Boolean);
}

export function deriveArchetypeDriverChips(profile) {
    return mapEntries(profile?.drivers, DRIVER_META);
}

export function deriveArchetypeHybridChips(profile) {
    return mapEntries(profile?.hybridSignals, HYBRID_META);
}

export function deriveArchetypeDisclosureChips(profile) {
    return mapEntries(profile?.podDisclose, DISCLOSURE_META);
}

export function deriveArchetypeTuningChips(profile) {
    return mapEntries(profile?.tuning, TUNING_META);
}

// ---- Headline --------------------------------------------------------

const POSTURE_COPY = {
    combatOriented: 'a combat-oriented build',
    comboOriented: 'a combo-oriented build',
    controlOriented: 'a control-oriented build',
    lockoutOriented: 'a lockout-oriented build',
    engineOriented: 'an engine-oriented build',
    disruptionOriented: 'a disruption-oriented build',
    kindredOriented: 'a typal build',
    hybrid: 'a hybrid build',
    undefined: null,
};

const CONFIDENCE_COPY = {
    strong: 'strongly',
    moderate: 'mainly',
    mixed: 'loosely',
    empty: null,
};

/**
 * Single-sentence elevator pitch derived from headline + concentration.
 * Returns `null` when the deck has no archetype signal.
 */
export function deriveArchetypeHeadline(profile) {
    const headline = profile?.headline;
    const primary = headline?.primary;
    if (!headline || !primary) return null;

    const posture = POSTURE_COPY[headline.posture] || POSTURE_COPY.hybrid;
    const adverb = CONFIDENCE_COPY[headline.confidence];
    const primaryLabel = primary.label || getPrettyLabel(primary.major) || 'this archetype';

    if (!adverb || !posture) {
        return `Reads as ${primaryLabel}.`;
    }

    const secondary = headline.secondary;
    if (secondary && headline.confidence === 'mixed') {
        const secondaryLabel = getPrettyLabel(secondary.major) || secondary.major;
        return `Blends ${primaryLabel} with ${secondaryLabel} as ${posture}.`;
    }

    return `Reads ${adverb} as ${primaryLabel} — ${posture}.`;
}

// ---- Enum-to-copy lookup ---------------------------------------------

const CONCENTRATION_COPY = {
    singleFocus: 'Single focus',
    hybridTwo: 'Two-major hybrid',
    hybridThree: 'Three-major hybrid',
    diffuse: 'Diffuse mix',
    empty: 'No archetype signal',
};

const DENSITY_COPY = {
    none: null,
    trace: 'Trace',
    mild: 'Mild',
    moderate: 'Moderate',
    heavy: 'Heavy',
    extreme: 'Extreme',
};

const TIER_COPY = {
    trace: 'Trace',
    supporting: 'Supporting',
    major: 'Major',
    dominant: 'Dominant',
};

export function archetypeEnumLabel(kind, value) {
    if (value == null) return null;
    switch (kind) {
        case 'concentration':
            return CONCENTRATION_COPY[value] || null;
        case 'density':
            return DENSITY_COPY[value] || null;
        case 'tier':
            return TIER_COPY[value] || null;
        case 'posture':
            return POSTURE_COPY[value] || null;
        case 'confidence':
            return CONFIDENCE_COPY[value] || null;
        default:
            return null;
    }
}
